// SPVolatilityMatrix.tsx
import React, { useEffect, useMemo, useState } from "react";
import DataGrid, { Column } from "devextreme-react/data-grid";
import "./SPMatrixTopGrid.css";
import { baseUrl } from "../../hooks/useFetch";
import { fmtVolMove } from "../../utils/formatUtils";

interface MatrixRow {
  vol_move: number | string | null;
  m30: number | null;
  m20: number | null;
  m10: number | null;
  p00: number | null;
  p10: number | null;
  p20: number | null;
  p30: number | null;
}

interface SPMatrixTopGridProps {
  cobStr: string | null | undefined;
}

function fmtNumK(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === "") return "";
  const x = Number(v);
  if (!Number.isFinite(x)) return "";

  const xk = x / 1000;

  const abs = Math.abs(xk);
  const decimals =
    abs >= 1000
      ? 0
      : abs >= 100
        ? 1
        : abs >= 10
          ? 1
          : 2;

  const s = abs.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });

  return xk < 0 ? `(${s})` : s;
}

const normalizeRow = (r: any): MatrixRow => ({
  vol_move: r.vol_move,
  m30: r["−30"] ?? r["-30"] ?? null,
  m20: r["−20"] ?? r["-20"] ?? null,
  m10: r["−10"] ?? r["-10"] ?? null,
  p00: r["0"] ?? r[0] ?? null,
  p10: r["10"] ?? r[10] ?? null,
  p20: r["20"] ?? r[20] ?? null,
  p30: r["30"] ?? r[30] ?? null,
});

const normalizeMatrix = (arr: any): MatrixRow[] =>
  Array.isArray(arr) ? arr.map(normalizeRow) : [];

function numKCellRender(cellData: any): React.ReactElement {
  const v = Number(cellData?.value);
  const text = fmtNumK(cellData?.value);
  const cls = !Number.isFinite(v) || v === 0 ? "" : v < 0 ? "neg" : "pos";
  return <span className={`num ${cls}`}>{text}</span>;
}

const SPMatrixTopGrid: React.FC<SPMatrixTopGridProps> = ({ cobStr }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [matrix, setMatrix] = useState<MatrixRow[]>([]);

  const effectiveCobStr = useMemo(
    () => (cobStr ? String(cobStr).trim() : ""),
    [cobStr]
  );

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);

    (async () => {
      try {
        const qs = new URLSearchParams();
        if (effectiveCobStr) qs.set("cob_date", effectiveCobStr);
        if (effectiveCobStr) qs.set("comparison_cob_date", effectiveCobStr);
        qs.set("measure", "PnL");

        const url = `${baseUrl}/market_risk/sp_matrix${qs.toString() ? `?${qs}` : ""}`;
        const r = await fetch(url, { signal: ac.signal });

        if (!r.ok) {
          let body = "";
          try {
            body = await r.text();
          } catch (e) {
            body = "";
          }
          console.error("sp HTTP error", r.status, body);
          return;
        }

        const j = await r.json();
        setMatrix(normalizeMatrix(j?.matrix));
      } catch (e: any) {
        if (e?.name !== "AbortError") console.error("sp fetch failed", e);
      } finally {
        setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [effectiveCobStr]);

  const onCellPrepared = (e: any): void => {
    if (e.rowType === "header") {
      e.cellElement.classList.add("sp-matrix__header-cell");
      return;
    }

    if (e.rowType !== "data") return;

    if (e.column?.dataField === "vol_move") {
      e.cellElement.classList.add("sp-matrix__label-cell");
      return;
    }

    const v = Number(e.value);
    if (!Number.isFinite(v)) return;

    if (v < 0) e.cellElement.classList.add("sp-matrix__neg");
  };

  return (
    <div className="topgrid">
      <DataGrid
        className="topgrid__grid dx-compact-grid"
        dataSource={matrix}
        showBorders
        width="100%"
        columnAutoWidth={true}
        columnMinWidth={70}
        allowColumnResizing={true}
        columnResizingMode="widget"
        wordWrapEnabled={false}
        showColumnLines
        showRowLines
        columnFixing={{ enabled: true }}
        hoverStateEnabled={false}
        rowAlternationEnabled={false}
        onCellPrepared={onCellPrepared}
      >
        <Column
          dataField="vol_move"
          caption="Vol move"
          alignment="center"
          width={70}
          fixed
          fixedPosition="left"
          allowSorting={false}
          allowFiltering={false}
          allowEditing={false}
          customizeText={(cell: any) => fmtVolMove(cell.value)}
        />

        <Column caption="Parallel price move" alignment="center">
          <Column
            dataField="m30"
            caption="-30.00%"
            alignment="right"
            minWidth={65}
            cellRender={numKCellRender}
          />
          <Column
            dataField="m20"
            caption="-20.00%"
            alignment="right"
            minWidth={65}
            cellRender={numKCellRender}
          />
          <Column
            dataField="m10"
            caption="-10.00%"
            alignment="right"
            minWidth={65}
            cellRender={numKCellRender}
          />
          <Column
            dataField="p00"
            caption="0.00%"
            alignment="right"
            minWidth={65}
            cellRender={numKCellRender}
          />
          <Column
            dataField="p10"
            caption="10.00%"
            alignment="right"
            minWidth={65}
            cellRender={numKCellRender}
          />
          <Column
            dataField="p20"
            caption="20.00%"
            alignment="right"
            minWidth={65}
            cellRender={numKCellRender}
          />
          <Column
            dataField="p30"
            caption="30.00%"
            alignment="right"
            minWidth={65}
            cellRender={numKCellRender}
          />
        </Column>
      </DataGrid>
    </div>
  );
};

export default SPMatrixTopGrid;
